import { FC, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Scanner, outlineTracker } from '@cs-delta/react-barcode-scanner';

import closeIcon from 'assets/vectors/close-icon.svg';
import { useTranslations } from 'hooks/useTranslations';
import {
  parseRegistrationQrCode,
  RegistrationQrCode
} from 'utils/productQrUtils';
import { showInfoToast } from 'utils/toastUtils';

import Heading, { Type as HeadingType } from 'components/UI/Heading';

import {
  Container,
  Header,
  IconButton,
  TextContainer,
  ScanContent
} from './styled';

type Props = {
  delay?: number;
  onClose: () => void;
  scanResult: (data: RegistrationQrCode) => void;
};

const BarcodeScanner: FC<Props> = ({ delay = 2000, onClose, scanResult }) => {
  const t = useTranslations();

  // Scan
  const onScan = useCallback(
    (data: DetectedBarcode[]) => {
      if (!data.length) {
        return null;
      }

      const identifiers = parseRegistrationQrCode(data[0].rawValue);
      if (!identifiers) {
        showInfoToast(t('errors.invalid_husqvarna_id'));
        return null;
      }

      scanResult(identifiers);
      onClose();
    },
    [scanResult, onClose, t]
  );

  // Error
  const onError = useCallback(
    (error: any) => {
      if (error) {
        onClose();
      }
    },
    [onClose]
  );

  return createPortal(
    <Container>
      <Header>
        <IconButton onClick={onClose}>
          <img src={closeIcon} alt="Close" />
        </IconButton>
      </Header>
      <TextContainer>
        <Heading type={HeadingType.h4}>{t('scanner.title')}</Heading>
        <p>{t('scanner.description')}</p>
      </TextContainer>
      <ScanContent>
        <Scanner
          onScan={onScan}
          onError={onError}
          config={{
            torch: true,
            zoom: true,
            frame: true,
            tracker: outlineTracker
          }}
          scanDelay={delay}
          allowMultiple
          sound
        />
      </ScanContent>
    </Container>,
    document.getElementById('scanner-root') as HTMLElement
  );
};

export default BarcodeScanner;
